import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Edit2, Trash2, Plus, Cpu, AlertCircle } from "react-feather";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  CreateTechnologyApi,
  DeleteTechnologyApi,
  EditTechnologyApi,
  listOfTechnologyApi,
} from "../../Api/TechnologyApi";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseModal from "../../BaseComponents/BaseModal";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import { Delete, Submit, Update } from "../../Constant";
import { checkStatusCodeSuccess } from "../../Constant/common";
import { DeleteMessage, SrNo } from "../../Constant/Center";
import {
  Actions,
  AddTechnology,
  DeleteTechnology,
  EditTechnology,
  Technology_Name,
  TechnologyName,
  TechnologyTitle,
} from "../../Constant/Technology";
import { InputPlaceHolder, validationMessages } from "../../Constant/validation";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { useTableQueryParams } from "../../utils/tableUtils";

const TechnologyValidationSchema = Yup.object().shape({
  technology_name: Yup.string().required(validationMessages.required(TechnologyName)),
});

const Technology = () => {
  useEffect(() => {
    document.title = TechnologyTitle;
  }, []);

  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5, columnName: "id", sortOrder: "desc" });

  const [technologyData, setTechnologyData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [technologyToDelete, setTechnologyToDelete] = useState(null);
  const [formData, setFormData] = useState({ technology_name: "" });

  const fetchTechnologyList = useCallback(() => {
    setLoading(true);
    listOfTechnologyApi({
      search: searchValue,
      order: [columnName, sortOrder?.toLowerCase()],
      pageSize: customPageSize,
      pageNumber: currentPage,
    })
      .then((r) => {
        if (checkStatusCodeSuccess(r?.statusCode)) {
          setTechnologyData(r?.data?.listOfTechnology || []);
          setTotalPages(r?.data?.totalPages || 1);
          setTotalRecords(r?.data?.totalRecordsCount || 0);
          setTotalNumberOfRows(r?.data?.numberOfRows || 0);
        } else {
          setTechnologyData([]);
          setTotalRecords(0);
          setTotalPages(1);
          setTotalNumberOfRows(0);
        }
      })
      .catch(() => setTechnologyData([]))
      .finally(() => setLoading(false));
  }, [searchValue, columnName, sortOrder, customPageSize, currentPage, setLoading]);

  useEffect(() => {
    fetchTechnologyList();
  }, [fetchTechnologyList]);

  const handleSubmitTechnology = (values, resetForm) => {
    setSubmitLoader(true);
    const payload = isEditMode
      ? { id: selectedId, technology_name: values.technology_name }
      : { technology_name: values.technology_name };
    const apiCall = isEditMode ? EditTechnologyApi(payload) : CreateTechnologyApi(payload);
    apiCall
      .then((r) => {
        if (checkStatusCodeSuccess(r?.statusCode)) {
          toast.success(r?.message);
          resetToFirstPage();
          setShowAddModal(false);
          resetForm();
          setIsEditMode(false);
          setSelectedId(null);
        } else toast.error(r?.message);
      })
      .catch((e) => toast.error(e?.response?.data?.message || e?.message))
      .finally(() => setSubmitLoader(false));
  };

  const handleDeleteTechnology = () => {
    if (!technologyToDelete) return;
    setDeleteLoader(true);
    DeleteTechnologyApi(technologyToDelete)
      .then((r) => {
        if (checkStatusCodeSuccess(r?.statusCode)) {
          toast.success(r?.message);
          resetToFirstPage();
          setShowDeleteModal(false);
        } else toast.error(r?.message);
      })
      .catch((e) => toast.error(e?.response?.data?.message || e?.message))
      .finally(() => {
        setDeleteLoader(false);
        setTechnologyToDelete(null);
      });
  };

  const columns = useMemo(
    () => [
      {
        header: SrNo,
        accessorKey: "serial_number",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        id: Technology_Name,
        header: TechnologyName,
        accessorKey: Technology_Name,
        enableColumnFilter: false,
      },
      {
        id: Actions.toLowerCase(),
        header: Actions,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <BaseTooltip label={EditTechnology} color="#b45309">
              <button
                onClick={() => {
                  setIsEditMode(true);
                  setSelectedId(row.original.id);
                  setFormData({ technology_name: row.original.technology_name });
                  setShowAddModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Edit2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={DeleteTechnology} color="#be123c">
              <button
                onClick={() => {
                  setTechnologyToDelete(row.original.id);
                  setShowDeleteModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(244,63,94,0.08)",
                  border: "1px solid rgba(244,63,94,0.2)",
                  color: "#f43f5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
          </div>
        ),
      },
    ],
    [currentPage, customPageSize],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(6,182,212,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Cpu size={20} strokeWidth={1.75} style={{ color: "#06b6d4" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {TechnologyTitle}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} technologies` : "Manage your technologies"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={() => {
            setIsEditMode(false);
            setFormData({ technology_name: "" });
            setShowAddModal(true);
          }}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {AddTechnology}
        </BaseButton>
      </div>

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {technologyData?.length > 0 || searchValue ? (
          <TableContainer
            data={technologyData}
            columns={columns}
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            fetchData={handlePageChange}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            manualPagination
            manualFiltering
            isGlobalFilter
            shouldLineBreak={false}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No technologies found</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <Formik
        enableReinitialize
        initialValues={formData}
        validationSchema={TechnologyValidationSchema}
        onSubmit={(values, { resetForm }) => handleSubmitTechnology(values, resetForm)}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <BaseModal
            isOpen={showAddModal}
            toggler={() => {
              if (!submitLoader) setShowAddModal(false);
            }}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: "rgba(6,182,212,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isEditMode ? (
                    <Edit2 size={14} strokeWidth={2} style={{ color: "#06b6d4" }} />
                  ) : (
                    <Plus size={14} strokeWidth={2.5} style={{ color: "#06b6d4" }} />
                  )}
                </div>
                <span>{isEditMode ? EditTechnology : AddTechnology}</span>
              </div>
            }
            submitText={isEditMode ? Update : Submit}
            submit={handleSubmit}
            loader={submitLoader}
            disabled={submitLoader}
          >
            <BaseInput
              label={TechnologyName}
              name={Technology_Name}
              value={values.technology_name}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched.technology_name}
              placeholder={InputPlaceHolder(TechnologyName)}
              error={touched.technology_name && errors.technology_name}
              required
            />
          </BaseModal>
        )}
      </Formik>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={showDeleteModal}
        title={DeleteTechnology}
        submitText={Delete}
        submit={handleDeleteTechnology}
        loader={deleteLoader}
        disabled={deleteLoader}
        toggler={() => setShowDeleteModal(false)}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteMessage}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default Technology;
